import { useRef } from "react";
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
  type MotionValue,
} from "framer-motion";
import { useMediaQuery } from "../hooks/useMediaQuery";

type AnimatedCharacterProps = {
  character: string;
  progress: MotionValue<number>;
  start: number;
  end: number;
  reduceMotion: boolean;
};

function AnimatedCharacter({
  character,
  progress,
  start,
  end,
  reduceMotion,
}: AnimatedCharacterProps) {
  const opacity = useTransform(progress, [start, end], [0.2, 1]);
  const visibleCharacter = character === " " ? "\u00a0" : character;

  return (
    <span className="relative inline-block" aria-hidden="true">
      <span className="text-transparent">{visibleCharacter}</span>
      <motion.span className="absolute inset-0" style={{ opacity: reduceMotion ? 1 : opacity }}>
        {visibleCharacter}
      </motion.span>
    </span>
  );
}

function ScrollAnimatedText({ text, className }: { text: string; className: string }) {
  const paragraphRef = useRef<HTMLParagraphElement>(null);
  const { scrollYProgress } = useScroll({
    target: paragraphRef,
    offset: ["start 0.8", "end 0.2"],
  });
  const characters = Array.from(text);

  return (
    <p ref={paragraphRef} className={className}>
      <span className="sr-only">{text}</span>
      <span aria-hidden="true">
        {characters.map((character, index) => (
          <AnimatedCharacter
            key={`${character}-${index}`}
            character={character}
            progress={scrollYProgress}
            start={index / characters.length}
            end={Math.min(1, (index + 1) / characters.length)}
            reduceMotion={false}
          />
        ))}
      </span>
    </p>
  );
}

export function AnimatedText({ text, className = "" }: { text: string; className?: string }) {
  const reduceMotion = useReducedMotion();
  const isPhone = useMediaQuery("(max-width: 767px)");

  if (reduceMotion || isPhone) {
    return <p className={className}>{text}</p>;
  }

  return <ScrollAnimatedText text={text} className={className} />;
}
